import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import DynamicFormPage from "../../components/DynamicFormPage";
import { useQuery } from "@tanstack/react-query";
import { getCategories } from "../../helpers/http_categories_requests";
import { getFormats } from "../../helpers/http_formats_requests";
import { getComforts } from "../../helpers/http_comforts_requests";
import { getPlatings } from "../../helpers/http_platings_requests";
import { getExternalFinishings } from "../../helpers/http_external_finishings_requests";
import { getProducts } from "../../helpers/http_products_requests";
import { getOrderDetails } from "../../helpers/http_orders_requests";
import { updateProductionClosing } from "../../helpers/http_production_requests";
import { getServices } from "../../helpers/http_services_requests";
import { ringSizeArray } from "../../utils/data_variables";
import { useCatalog } from "../../providers/CatalogProvider";
import { centsToReais, formatCurrency, mapExtraServices, reaisToCents } from "../../utils/functions";

export default function ProductionClosing() {

    const { id } = useParams();
    const { metalList } = useCatalog();

    const formatOptions = (data) => data?.data?.data?.data?.map(item => ({ value: item.id, label: item.name }));

    const categoriesQuery = useQuery({ queryKey: ['getCategories'], queryFn: getCategories, staleTime: 1000 * 5 });
    const formatsQuery = useQuery({ queryKey: ['getFormats'], queryFn: getFormats, staleTime: 1000 * 5 });
    const comfortsQuery = useQuery({ queryKey: ['getComforts'], queryFn: getComforts, staleTime: 1000 * 5 });
    const platingsQuery = useQuery({ queryKey: ['getPlatings'], queryFn: getPlatings, staleTime: 1000 * 5 });
    const externalFinishingsQuery = useQuery({ queryKey: ['getExternalFinishings'], queryFn: getExternalFinishings, staleTime: 1000 * 5 });
    const productsQuery = useQuery({ queryKey: ['getProducts'], queryFn: getProducts });
    const servicesQuery = useQuery({ queryKey: ['getServices'], queryFn: getServices, staleTime: 1000 * 15 });

    const orderQuery = useQuery({ queryKey: ['getOrderDetails', id], queryFn: () => getOrderDetails(id) });

    const queries = [categoriesQuery, formatsQuery, comfortsQuery, platingsQuery, externalFinishingsQuery, productsQuery, orderQuery, servicesQuery];

    const isFetching = queries.some(query => query.isFetching);

    const categories = formatOptions(categoriesQuery);
    const formats = formatOptions(formatsQuery);
    const comforts = formatOptions(comfortsQuery);
    const platings = formatOptions(platingsQuery);
    const externalFinishings = formatOptions(externalFinishingsQuery);

    const { data: orderData } = orderQuery?.data?.data ?? {};
    const { data: services } = servicesQuery?.data?.data ?? {};

    const navigate = useNavigate();

    const currentExtraServices = mapExtraServices(services);

    const productFormFieldList = [
        {
            title: "Dados de Produto",
            fields: [
                { name: "name", label: "Nome", type: "text", required: true, disabled: true },
                { name: "category", label: "Categoria", type: "select", required: true, options: categories, disabled: true },
                { name: "selected_metal", label: "Material", type: "select", required: true, options: metalList, disabled: true },
                { name: "format", label: "Formato", type: "select", options: formats, disabled: true },
                { name: "confort", label: "Conforto", type: "select", options: comforts, disabled: true },
                { name: "height", label: "Altura", type: "number", end_adornment: "mm", disabled: true },
                { name: "width", label: "Largura", type: "number", end_adornment: "mm", disabled: true },
                { name: "thickness", label: "Espessura", type: "number", end_adornment: "mm", disabled: true },
                { name: "plating", label: "Banho", type: "select", options: platings, disabled: true },
                { name: "finishing", label: "Acabamento Externo", type: "select", options: externalFinishings, disabled: true },
                { name: "is_crafted", label: "Joia trabalhada / montada", type: "check" },
            ]
        },
        {
            title: "Serviços e Extras",
            fields: [
                {
                    type: 'extras',
                    name: 'extras',
                    label: 'Serviços Extras',
                    options: currentExtraServices || [],
                    fullWidth: true,
                    getOptionLabel: (option) => `${option.label} (${formatCurrency(option.price)})`
                }
            ]
        },
        {
            title: "Tamanhos e Gravações",
            fields: [
                { name: "size_one", label: "Tamanho joia 1", type: "select", options: ringSizeArray },
                { name: "engraving_one", label: "Gravação joia 1", type: "text", },
                { name: "size_two", label: "Tamanho joia 2", type: "select", options: ringSizeArray },
                { name: "engraving_two", label: "Gravação joia 2", type: "text", },
            ]
        },
        {
            title: "Pesos de Produto",
            fields: [
                { name: "high_gold_weight", label: "Peso em Ouro 18K", type: "number", required: true, end_adornment: "g" },
                { name: "low_gold_weight", label: "Peso em Ouro 10K", type: "number", required: true, end_adornment: "g" },
                { name: "silver_weight", label: "Peso em Prata 950", type: "number", required: true, end_adornment: "g" },
            ]
        },
        {
            title: "Datas e Financeiro",
            fields: [
                { name: "ordered_at", label: "Data de Pedido", type: "date_time_picker", required: true, disabled: true },
                { name: "production_date", label: "Data de Produção", type: "date_time_picker", required: true, disabled: true },
                { name: "delivery_date", label: "Data de Entrega", type: "date_time_picker", required: true },
                { name: "price", label: "Valor final", type: "text", mask: "currency", required: true, fullWidth: true },
            ]
        },
    ];

    const serviceFormFieldList = [
        {
            title: "Dados de Produto",
            fields: [
                { name: "name", label: "Nome", type: "text", required: true, disabled: true },
                { name: "selected_metal", label: "Material", type: "select", required: true, options: metalList, disabled: true },
            ]
        },
        {
            title: "Ficha Técnica",
            fields: [
                { name: "self_reference", label: "Referência Interna", type: "text", required: true, disabled: true },
                { name: "prod_time", label: "Tempo Ideal de Produção", type: "number", required: true, end_adornment: "dias", disabled: true },
                { name: "production_description", label: "Descrição para Produção", type: "text", multiline: true, rows: 5, fullWidth: true, disabled: true },
                { name: "prod_steps_json", label: "Passos de Produção", type: "steps", fullWidth: true, disabled: true },
            ]
        },
        {
            title: "Data de Entrega",
            fields: [
                { name: "delivery_date", label: "Data de Entrega", type: "date_time_picker", required: true },
            ]
        },

    ];


    const formFieldList = orderData?.is_service ? serviceFormFieldList : productFormFieldList;


    const handleSubmit = (data) => {

        const payload = { ...data, production_act: 1, price: reaisToCents(data.price) }

        updateProductionClosing(payload, () => navigate('/producao/fechamento'));
    };

    const formInitialData = orderData
        ? { ...orderData, price: centsToReais(orderData.price) }
        : orderData;

    return <DynamicFormPage multiImageUpload isLoading={isFetching} initialData={formInitialData} title={"Produção - Fechamento"} formFieldList={formFieldList} verification onCancel={() => navigate(-1)} onSubmit={handleSubmit} />;
};